/**
 * dt-select - Dropdown select component
 * Features: options array, placeholder, error state, keyboard navigation
 */
import { BaseElement } from '../base-element';

export interface SelectOption {
  value: string;
  label: string;
}

export class DtSelect extends BaseElement {
  static observedAttributes = ['label', 'placeholder', 'value', 'error', 'required', 'disabled'];

  private _label = '';
  private _placeholder = 'Select...';
  private _value = '';
  private _options: SelectOption[] = [];
  private _error: string | null = null;
  private _required = false;
  private _disabled = false;
  private _isOpen = false;
  private _focusedIndex = -1;
  private _touched = false;
  private _validationMessage: string | null = null;
  private _restoringFocus = false;
  private _selectId = `dt-select-${Math.random().toString(36).slice(2, 9)}`;

  get label(): string {
    return this._label;
  }
  set label(value: string) {
    this._label = value;
    this.updateRender();
  }

  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.updateRender();
  }

  get value(): string {
    return this._value;
  }
  set value(val: string) {
    this._value = val;
    this.updateRender();
  }

  get options(): SelectOption[] {
    return this._options;
  }
  set options(val: SelectOption[]) {
    this._options = val;
    this.updateRender();
  }

  get error(): string | null {
    return this._error;
  }
  set error(value: string | null) {
    this._error = value;
    this.updateRender();
  }

  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = value;
    this.updateRender();
  }

  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = value;
    this.updateRender();
  }

  private _validate(): boolean {
    if (this._required && this._touched && !this._value) {
      this._validationMessage = 'Please select an option';
    } else {
      this._validationMessage = null;
    }
    return !this._validationMessage;
  }

  validate(): boolean {
    this._touched = true;
    const result = this._validate();
    this.updateRender();
    return result && !this._error;
  }

  connectedCallback(): void {
    this.updateRender();
    document.addEventListener('click', this.handleDocumentClick);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleDocumentClick);
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    switch (name) {
      case 'label':
        this._label = newValue || '';
        break;
      case 'placeholder':
        this._placeholder = newValue || 'Select...';
        break;
      case 'value':
        this._value = newValue || '';
        break;
      case 'error':
        this._error = newValue;
        break;
      case 'required':
        this._required = newValue !== null && newValue !== 'false';
        break;
      case 'disabled':
        this._disabled = newValue !== null && newValue !== 'false';
        break;
    }
    this.updateRender();
  }

  private handleDocumentClick = (e: Event): void => {
    const path = e.composedPath?.() ?? [];
    if (!path.includes(this)) {
      this.closeDropdown();
    }
  };

  private handleTriggerBlur = (): void => {
    if (this._restoringFocus) return;
    // Defer blur-triggered re-renders so the browser finishes Tab
    // navigation before we replace innerHTML.
    if (this._isOpen) {
      queueMicrotask(() => this.closeDropdown());
    } else {
      this._touched = true;
      const hadValidation = this._validationMessage;
      this._validate();
      if (this._validationMessage !== hadValidation) {
        queueMicrotask(() => this.updateRender());
      }
    }
  };

  private toggleDropdown = (): void => {
    if (this._disabled) return;
    this._isOpen = !this._isOpen;
    this._focusedIndex = this._isOpen
      ? this._options.findIndex((o) => o.value === this._value)
      : -1;
    this.updateRender();
  };

  private closeDropdown(): void {
    if (this._isOpen) {
      this._isOpen = false;
      this._focusedIndex = -1;
      this._touched = true;
      this._validate();
      this.updateRender();
    }
  }

  private selectOption(option: SelectOption): void {
    this._value = option.value;
    this._isOpen = false;
    this._focusedIndex = -1;
    this._touched = true;
    this._validate();
    this.updateRender();
    this.emit('change', { value: this._value });
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    if (this._disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (this._isOpen && this._focusedIndex >= 0) {
          this.selectOption(this._options[this._focusedIndex]);
        } else {
          this.toggleDropdown();
        }
        break;
      case 'Escape':
        this.closeDropdown();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!this._isOpen) {
          this._isOpen = true;
          this._focusedIndex = 0;
        } else {
          this._focusedIndex = Math.min(this._focusedIndex + 1, this._options.length - 1);
        }
        this.updateRender();
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (this._isOpen) {
          this._focusedIndex = Math.max(this._focusedIndex - 1, 0);
          this.updateRender();
        }
        break;
    }
  };

  private getSelectedLabel(): string {
    const selected = this._options.find((o) => o.value === this._value);
    return selected?.label || this._placeholder;
  }

  private updateRender(): void {
    const effectiveError = this._error || this._validationMessage;
    const hasError = !!effectiveError;
    const hasValue = !!this._value;
    const prevTrigger = this.$('.trigger');
    const hadFocus = prevTrigger && this.ownerDocument.activeElement === prevTrigger;

    // Suppress blur handler BEFORE render — removing a focused element from DOM
    // fires blur synchronously, which would trigger closeDropdown() mid-render.
    if (hadFocus) {
      this._restoringFocus = true;
    }

    const labelColorClass = hasError ? 'text-error-500' : this._isOpen ? 'text-primary-500' : 'text-neutral-700';
    const triggerBorderClass = hasError
      ? 'border-error-500 focus:ring-2 focus:ring-error-500'
      : this._isOpen
        ? 'border-primary-500 ring-4 ring-primary-500/10'
        : 'border-neutral-300 focus:ring-2 focus:ring-primary-500';

    this.render(`
      <div class="group flex flex-col ${this._disabled ? 'opacity-50' : ''} ${this._isOpen ? 'relative z-50' : 'relative z-0'}">
        ${
          this._label
            ? `
          <label class="block text-sm font-medium ${labelColorClass} mb-1.5 ml-1" id="${this._selectId}-label">
            ${this.esc(this._label)}${this._required ? '<span class="ml-1 text-error-500" aria-hidden="true">*</span>' : ''}
          </label>
        `
            : ''
        }
        <div class="relative">
          <div
            class="trigger flex items-center justify-between w-full rounded-lg border ${triggerBorderClass} bg-white shadow-sm h-12 px-4 transition-colors ${this._disabled ? 'cursor-not-allowed' : 'cursor-pointer'} focus:outline-none"
            role="combobox"
            tabindex="${this._disabled ? '-1' : '0'}"
            aria-haspopup="listbox"
            aria-expanded="${this._isOpen}"
            aria-labelledby="${this._selectId}-label"
            ${this._disabled ? 'aria-disabled="true"' : ''}
            ${hasError ? 'aria-invalid="true"' : ''}
          >
            <span class="truncate ${hasValue ? 'text-neutral-900' : 'text-neutral-500'}">${this.esc(this.getSelectedLabel())}</span>
            <svg class="chevron h-6 w-6 text-neutral-500 transition-transform ${this._isOpen ? 'rotate-180' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
          ${
            this._isOpen
              ? `
            <div class="dropdown absolute mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg py-1 z-50" role="listbox" tabindex="-1" aria-labelledby="${this._selectId}-label">
              ${this._options
                .map(
                  (opt, idx) => `
                <div 
                  class="option relative py-3 pl-4 pr-9 select-none transition-colors hover:bg-neutral-100 ${opt.value === this._value ? 'bg-primary-500/10 text-primary-500 font-medium' : 'text-neutral-900 font-normal'} ${idx === this._focusedIndex && opt.value !== this._value ? 'bg-primary-50 ring-2 ring-inset ring-primary-500/50' : ''}${idx === this._focusedIndex && opt.value === this._value ? 'ring-2 ring-inset ring-primary-500/50' : ''}"
                  role="option"
                  data-value="${this.esc(opt.value)}"
                  aria-selected="${opt.value === this._value}"
                >
                  <span class="truncate">${this.esc(opt.label)}</span>
                  ${
                    opt.value === this._value
                      ? `
                    <svg class="check-icon absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  `
                      : ''
                  }
                </div>
              `
                )
                .join('')}
            </div>
          `
              : ''
          }
        </div>
        ${
          hasError
            ? `
          <p class="mt-1.5 ml-1 text-xs text-error-500 flex items-center gap-1" role="alert">
            ${this.esc(effectiveError!)}
          </p>
        `
            : ''
        }
      </div>
    `);

    // Attach event listeners
    const trigger = this.$('.trigger');
    if (trigger) {
      trigger.addEventListener('click', this.toggleDropdown);
      trigger.addEventListener('keydown', this.handleKeydown);
      trigger.addEventListener('blur', this.handleTriggerBlur);

      if (hadFocus) {
        trigger.focus();
        this._restoringFocus = false;
      }
    } else if (hadFocus) {
      this._restoringFocus = false;
    }

    const options = this.$$('.option');
    options.forEach((opt) => {
      opt.addEventListener('click', () => {
        const value = opt.getAttribute('data-value') || '';
        const option = this._options.find((o) => o.value === value);
        if (option) {
          this.selectOption(option);
        }
      });
    });
  }
}

customElements.define('dt-select', DtSelect);
